import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as ArrowLeftButton } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRightButton } from 'src/Images/icons/common/arrowright.svg';

export default function TabView({ tabDocumentComponents, onTabChange }) {
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const userPermissions = [
    ...(authReducer.assignedPermissionsForCurrentUser?.positionPermissions?.map(
      (permission) => permission.codename,
    ) || []),
    ...(authReducer.assignedPermissionsForCurrentUser?.teamPermissions?.map(
      (permission) => permission.codename,
    ) || []),
  ];

  const validComponents = tabDocumentComponents.filter(
    (section) =>
      userPermissions.length === 0 ||
      !section.permission ||
      userPermissions.includes(section.permission),
  );

  const [activeTab, setActiveTab] = useState('0');
  const [isOverflowing, setIsOverflowing] = useState(false);
  const tabScrollRef = useRef(null);

  const scrollTabs = useCallback((direction) => {
    const scrollAmount = 150;

    tabScrollRef.current?.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    let rafId;

    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!tabScrollRef.current) return;
        const { scrollWidth, clientWidth } = tabScrollRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [validComponents]);

  const handleTabClick = (index) => {
    setActiveTab(index.toString());
    if (onTabChange) onTabChange();
  };

  return (
    <div className="div-flex-column">
      <div className="div-flex-row position-relative border-bottom tab-view-bg position-fixed w-100">
        {isOverflowing && (
          <button
            type="button"
            className={`scroll-button left border-none theme-color-${themeColor}`}
            onClick={() => scrollTabs('left')}
            aria-label="Scroll left"
          >
            <div className="icon-wrapper div-flex-row div-align-center div-flex-center">
              <ArrowLeftButton className="icon-size-sm" />
            </div>
          </button>
        )}
        <div
          ref={tabScrollRef}
          className={`tab-scroll-wrapper div-flex-row no-scrollbar ${
            !isOverflowing ? 'no-side-padding' : ''
          }`}
        >
          {validComponents.map((section, index) => (
            <div
              key={section.id}
              onClick={() => handleTabClick(index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleTabClick(index);
              }}
              role="button"
              tabIndex={0}
              className={`div-flex-row div-align-center text-sm-outfit text-medium-bold cg-5 tab-selection ${
                activeTab === index.toString()
                  ? `theme-tab-active-${themeColor} theme-text-color-${themeColor}`
                  : 'secondary-text-grey'
              }`}
            >
              <span
                className={`text-nowrap ${
                  section.indicator ? 'pulse-text' : ''
                }`}
              >
                {section.title}
              </span>
            </div>
          ))}
        </div>
        {isOverflowing && (
          <button
            type="button"
            className={`scroll-button right border-none theme-color-${themeColor}`}
            onClick={() => scrollTabs('right')}
            aria-label="Scroll right"
          >
            <div className="icon-wrapper div-flex-row div-align-center div-flex-center">
              <ArrowRightButton className="icon-size-sm" />
            </div>
          </button>
        )}
      </div>
      <div className="tab-padding-top">
        {validComponents.map(
          (section, index) =>
            activeTab === index.toString() && (
              <div key={section.id}>{section.component}</div>
            ),
        )}
      </div>
    </div>
  );
}
